import { Box, Button, LinearProgress, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { widgetStyles } from '../common/Widget'
import AssignmentIcon from '@mui/icons-material/Assignment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useMosInfo from '../common/provider/useMosInfo'
import { apiGet } from '../../../generic/Api_Functions'
import InspectionReportDialog from './InspectionReportDialog'

const InspectionWidget = (props) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const styles = widgetStyles(theme)
  const { fabricationOrder, taskReference, device, jobReference, inspectionReportVersion } = useMosInfo()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState([])
  const [reportedFeaturesQuantity, setReportedFeaturesQuantity] = useState(0)
  const loadRequestIdRef = useRef(0)

  const canInspect = useMemo(() => {
    return taskReference?.id && fabricationOrder?.id && jobReference?.id && device?.id
  }, [taskReference, fabricationOrder, jobReference, device])

  const loadData = useCallback(async () => {
    if (!taskReference) {
      return
    }
    // Tag this request so a slower response from a previously selected task
    // cannot overwrite the data of the task that is currently selected.
    const requestId = ++loadRequestIdRef.current
    const result = await apiGet('taskReferenceFeature/' + taskReference.id)
    if (requestId !== loadRequestIdRef.current) {
      return
    }
    // The endpoint now returns feature groups ({ id, code, name, description, features })
    // instead of a flat feature list, so flatten to compute the reported progress.
    const allFeatures = result.flatMap((group) => group.features || [])
    let reported = 0
    for (const item of allFeatures) {
      if (item.measuredValue !== null || item.featureType?.dataType === 'NO_RESPONSE') {
        reported++
      }
    }
    setReportedFeaturesQuantity(reported)
    setData(result)
  }, [taskReference])

  // `inspectionReportVersion` (not the local `open` state) is what triggers a reload after a report:
  // the dialog can be opened from another widget, and then this widget's `open` never changes.
  useEffect(() => {
    if (taskReference) {
      loadData()
    } else {
      // Invalidate any in-flight request so its late response is discarded.
      loadRequestIdRef.current++
      setData([])
      setReportedFeaturesQuantity(0)
    }
  }, [fabricationOrder, taskReference, loadData, inspectionReportVersion])

  const totalFeaturesQuantity = useMemo(() => {
    return data.reduce((total, group) => total + (group.features?.length || 0), 0)
  }, [data])

  const progressValue = useMemo(() => {
    return totalFeaturesQuantity > 0 ? (reportedFeaturesQuantity / totalFeaturesQuantity) * 100 : 0
  }, [totalFeaturesQuantity, reportedFeaturesQuantity])

  const disabledReason = useMemo(() => {
    if (!fabricationOrder || !taskReference || !device || !jobReference) {
      return t_('Select a task reference')
    }
    if (totalFeaturesQuantity === 0) {
      return t_('No inspection features assigned to this task')
    }
    if (canInspect) {
      return t_('Inspection report is not available yet')
    }
    return ''
  }, [canInspect, totalFeaturesQuantity, device, fabricationOrder, jobReference, t_, taskReference])

  return (
    <>
      <InspectionReportDialog open={open} onClose={() => setOpen(false)} />
      <Stack sx={{ height: '100%', gap: 1 }}>
        <Stack>
          <Typography sx={styles.subtitle2}>{t_('Inspection Progress')}</Typography>
        </Stack>
        <Stack direction='row' alignItems='center' sx={{ width: '100%', minWidth: 0 }}>
          {!fabricationOrder || !taskReference || !device || !jobReference
            ? (
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0, mt: 1, mr: 1 }}>
                <Typography variant='body2' color='text.secondary'>{t_('No data to display')}</Typography>
                <Typography variant='body2' color='text.secondary'>{t_('Select a task reference')}</Typography>
              </Stack>
              )
            : (
              <>
                <Stack direction='column' spacing={1} sx={{ flex: 1, minWidth: 0, mt: 1, mr: 1 }}>
                  <Tooltip title={taskReference?.name}>
                    <Typography variant='body2' color='text.secondary' noWrap>
                      {t_('Operation')} {taskReference?.name}
                    </Typography>
                  </Tooltip>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant='caption' color='text.secondary'>{t_('Progress')}</Typography>
                      <Typography variant='caption' fontWeight='bold'>
                        {reportedFeaturesQuantity} / {totalFeaturesQuantity}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant='determinate'
                      value={progressValue}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    {disabledReason && (totalFeaturesQuantity === 0 || !canInspect)
                      ? <Typography variant='caption' color='warning.main'>{disabledReason}</Typography>
                      : null}
                  </Box>
                </Stack>
                <Tooltip title={disabledReason}>
                  <Box component='span' sx={{ flexShrink: 0 }}>
                    <Button
                      onClick={() => setOpen(true)}
                      disabled={totalFeaturesQuantity === 0 || !canInspect}
                      sx={{
                        mt: 1,
                        flexShrink: 0,
                        borderRadius: '12px',
                        border: '1px solid ' + theme.palette.divider,
                        width: 44,
                        height: 44,
                        backgroundColor: theme.palette.background.button,
                        '&:hover': { backgroundColor: theme.palette.background.buttonHover }
                      }}
                    >
                      Open
                    </Button>
                  </Box>
                </Tooltip>
              </>
              )}
        </Stack>
      </Stack>
    </>
  )
}

class InspectionWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'InspectionWidgetProvider'

  getName = () => 'Inspection'

  getIcon = () => AssignmentIcon

  createWidget = (props) => <InspectionWidget {...props} />

  createPreviewWidget = () => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          gap: 12
        }}
      >
        <Typography variant='body2'>Inspection Report</Typography>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LinearProgress
            variant='determinate'
            value={30}
            sx={{ borderRadius: 2, flex: 1 }}
          />

          <Button size='small' variant='outlined'>
            Open
          </Button>
        </div>
      </div>
    )
  }

  size = { defaultW: 2, defaultH: 3, minW: 2, minH: 3 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export default InspectionWidgetProvider
